import * as React from 'react';
import strings from '../../../../strings/strings.json';
import EditableModuleList from '../EditableModuleList';
import { ErrorMessage, FormCard } from 'components/forms/FormComponents';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { FlexColMd, FlexEnd, FlexSmAlignCenter } from 'components/Elements';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { Course } from 'models/Course';
import WarningIcon from 'assets/icons/indicators/WarningIcon';
import { RightNavButtonContainer } from '../../shared-styles/courseBuilderFormComponents';
import NavButton from 'components/buttons/NavButton';
import styled from 'styled-components';
import { ContentComponent } from './CourseStructureAndContentComponent';
import Modal from 'components/modals/Modal';
import CreateModuleForm from '../moduleForms/CreateModuleForm';
import Button from 'components/buttons/Button';

type ModulesListProps = {
    activeModuleId?: string;
    setComponent: (value: React.SetStateAction<ContentComponent>) => void;
    goToGeneratedContent: () => void;
};

export default function ModulesList({
    activeModuleId,
    setComponent,
    goToGeneratedContent,
}: ModulesListProps): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { showIncompleteStepsWarning, editMode } = React.useContext(BuildCourseContext);

    const [openAddModuleModal, setOpenAddModuleModal] = React.useState<boolean>();

    const addModuleButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (course.modules.length === 0) {
            addModuleButtonRef.current?.focus();
        }
    }, [course]);

    function showEditActivity(activityId: string) {
        setComponent({ name: 'editActivity', activityId: activityId });
    }

    const addModuleButton = (
        <Button
            ref={addModuleButtonRef}
            title={strings.courseContentPage.addModuleButton}
            aria={strings.courseContentPage.addModuleButtonAria}
            onClick={() => setOpenAddModuleModal(true)}
            colour={course.modules.length > 0 ? 'SECONDARY' : 'PRIMARY'}
        />
    );

    if (openAddModuleModal) {
        return (
            <Modal
                headlineText={strings.moduleForm.createModuleTitle}
                onClickCloseButton={() => setOpenAddModuleModal(false)}
                modalBody={
                    <CreateModuleForm
                        closeForm={(moduleId: string) => {
                            setOpenAddModuleModal(false);
                            setComponent({ name: 'modulesList', moduleId: moduleId });
                        }}
                    />
                }
            />
        );
    }

    function EveryModuleHasActivity(course: Course): boolean {
        return course.modules.length >= 1 && course.modules.every((m) => m.activities.length >= 1);
    }

    const everyModuleHasActivity = EveryModuleHasActivity(course);

    if (course.modules.length > 0) {
        return (
            <CourseContentPageContent>
                {!everyModuleHasActivity && showIncompleteStepsWarning && (
                    <NotificationContainer>
                        <Notification
                            title={strings.courseEditorPage.incompleteModuleWarning.title}
                            description={strings.courseEditorPage.incompleteModuleWarning.description}
                            notificationType={NotificationType.WARNING}
                            maxWidth="100%"
                        />
                    </NotificationContainer>
                )}
                <EditableModuleList
                    courseId={course.id}
                    modules={course.modules}
                    activeModuleId={activeModuleId}
                    editActivityClick={showEditActivity}
                    showIncompleteStepsWarning={showIncompleteStepsWarning}
                    coursePublished={false}
                    setComponent={setComponent}
                />
                {!editMode && (
                    <>
                        <AddModuleButtonRow>{addModuleButton}</AddModuleButtonRow>
                        <RightNavButtonContainer>
                            <NavButton
                                title={strings.courseContentPage.generateContentNavButton.title}
                                aria={strings.courseContentPage.generateContentNavButton.aria}
                                onClick={goToGeneratedContent}
                            />
                        </RightNavButtonContainer>
                    </>
                )}
            </CourseContentPageContent>
        );
    } else {
        return (
            <CourseContentPageContent>
                <NoModulesCard>
                    <FlexSmAlignCenter>
                        <WarningIcon />
                        <ErrorMessage>{strings.courseContentPage.noModules}</ErrorMessage>
                    </FlexSmAlignCenter>
                    {addModuleButton}
                </NoModulesCard>
                <RightNavButtonContainer>
                    <NavButton
                        title={strings.courseContentPage.generateContentNavButton.title}
                        aria={strings.courseContentPage.generateContentNavButton.aria}
                        onClick={goToGeneratedContent}
                    />
                </RightNavButtonContainer>
            </CourseContentPageContent>
        );
    }
}

const AddModuleButtonRow = styled(FlexEnd)`
    width: 100%;
`;

const NoModulesCard = styled(FormCard).attrs({ as: 'div' })`
    align-items: center;
    width: 100%;
`;

const CourseContentPageContent = styled(FlexColMd)`
    align-items: flex-start;
    width: 100%;
    max-width: 900px;
`;

const NotificationContainer = styled.div`
    width: 100%;
`;
